/** Default staff/doctor profile form values (all optional). */

export function emptyDesignation() {
  return {
    title: '',
    organization: '',
    from_date: '',
    to_date: '',
    is_current: false,
  };
}

/** Prefer designations[]; migrate legacy designation string. */
export function normalizeDesignations(user) {
  const u = user || {};
  if (Array.isArray(u.designations) && u.designations.length) {
    return u.designations.map((d) => ({
      title: d.title || '',
      organization: d.organization || '',
      from_date: d.from_date || '',
      to_date: d.to_date || '',
      is_current: Boolean(d.is_current),
    }));
  }
  if (u.designation) {
    return [{ ...emptyDesignation(), title: u.designation, is_current: true }];
  }
  return [];
}

function isGeneralPhysicianSpec(spec) {
  return /^(general\s*physician|general\s*medicine|general\s*practitioner)$/i.test(
    String(spec || '').trim(),
  );
}

export function emptyProfileForm(user) {
  const u = user || {};
  const privacy = u.privacy || {};
  const booking = u.booking || {};
  const gp = isGeneralPhysicianSpec(u.specialization);
  return {
    name: u.name || '',
    title: u.title || '',
    phone: u.phone || '',
    specialization: u.specialization || '',
    secondary_specialization: u.secondary_specialization || '',
    qualifications: u.qualifications || '',
    registration_number: u.registration_number || '',
    medical_council: u.medical_council || '',
    designations: normalizeDesignations(u),
    department: u.department || '',
    category: u.category || '',
    fellowship: u.fellowship || '',
    clinical_interests: u.clinical_interests || '',
    opd_days: u.opd_days || '',
    consultation_fee: u.consultation_fee ?? '',
    bio: u.bio || '',
    languages: u.languages || '',
    experience_years: u.experience_years ?? '',
    photo_url: u.photo_url || '',
    privacy: {
      show_on_portal: privacy.show_on_portal !== false,
      show_photo: privacy.show_photo !== false,
      show_bio: privacy.show_bio !== false,
      show_qualifications: privacy.show_qualifications !== false,
      show_specialization: privacy.show_specialization !== false,
      show_title: privacy.show_title !== false,
      show_phone: privacy.show_phone === true,
    },
    booking: {
      accept_appointments: booking.accept_appointments !== false,
      // Online portal booking defaults on for GPs only; specialists off unless set.
      patient_portal_booking: booking.patient_portal_booking != null
        ? booking.patient_portal_booking !== false
        : gp,
      can_provide_referral: booking.can_provide_referral === true,
      booking_mode: booking.booking_mode ?? null,
      slot_minutes: booking.slot_minutes ?? null,
      max_appointments_per_day: booking.max_appointments_per_day ?? null,
      max_walkins_per_day: booking.max_walkins_per_day ?? null,
      patient_book_hours_prior: booking.patient_book_hours_prior ?? null,
      patient_cancel_hours_prior: booking.patient_cancel_hours_prior ?? null,
    },
  };
}

export function isPatientUser(user) {
  return (user?.role || '').toLowerCase() === 'patient'
    || (user?.role_name || '').toLowerCase() === 'patient';
}

export function isActiveUser(user) {
  return user?.is_active !== false;
}

export function toProfilePayload(form) {
  const years = form.experience_years === '' || form.experience_years == null
    ? null
    : Number(form.experience_years);
  const fee = form.consultation_fee === '' || form.consultation_fee == null
    ? null
    : Number(form.consultation_fee);
  const designations = (form.designations || [])
    .filter((d) => (d.title || '').trim())
    .map((d) => ({
      title: d.title.trim(),
      organization: (d.organization || '').trim(),
      from_date: (d.from_date || '').trim(),
      to_date: d.is_current ? '' : (d.to_date || '').trim(),
      is_current: Boolean(d.is_current),
    }));
  const currentTitle = designations.find((d) => d.is_current)?.title
    || designations[0]?.title
    || '';
  return {
    name: form.name.trim() || undefined,
    title: form.title,
    phone: form.phone,
    specialization: form.specialization || undefined,
    secondary_specialization: form.secondary_specialization,
    qualifications: form.qualifications,
    registration_number: form.registration_number,
    medical_council: form.medical_council,
    designations,
    designation: currentTitle,
    department: form.department,
    category: form.category,
    fellowship: form.fellowship,
    clinical_interests: form.clinical_interests,
    opd_days: form.opd_days,
    consultation_fee: Number.isFinite(fee) ? fee : null,
    bio: form.bio,
    languages: form.languages,
    experience_years: Number.isFinite(years) ? years : null,
    photo_url: form.photo_url,
    privacy: { ...form.privacy },
    booking: { ...form.booking },
  };
}
